import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { useSEO } from "@/lib/useSEO";

// Investor signup — lets investors create an account, pick the sectors they
// fund, and then land on /app/investor-feed where founder projects appear
// GOLD-tier (category match) first, then SILVER (out-of-domain).
export default function InvestorJoin() {
  useSEO({
    title: "Maqam Investors — Surface KSA-quality dealflow",
    description: "Founder projects routed to investors who fund their category. Gold-tier matching. Built in Riyadh.",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", firm: "",
    categories: [], check_min: 50000, check_max: 500000, bio: "",
    stage_focus: ["mvp", "revenue"], risk_appetite: "balanced",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/investors/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "investor") navigate("/app/investor-feed");
  }, [user, navigate]);

  const toggleCat = (key) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(key) ? f.categories.filter((k) => k !== key) : [...f.categories, key],
    }));
  };

  const toggleStage = (key) => {
    setForm((f) => ({
      ...f,
      stage_focus: f.stage_focus.includes(key) ? f.stage_focus.filter((k) => k !== key) : [...f.stage_focus, key],
    }));
  };

  const register = async () => {
    if (!form.full_name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Name, email, and an 8+ char password required."); return;
    }
    if (form.categories.length === 0) {
      toast.error("Pick at least one category you invest in."); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/investors/register", form);
      localStorage.setItem("jf_token", r.data.token);
      toast.success(`Welcome, ${r.data.user.full_name}.`);
      window.location.assign("/app/investor-feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white" data-testid="investor-join-page">
      <PublicHeader />
      <section
        className="relative overflow-hidden"
        style={{ background: "radial-gradient(ellipse 100% 85% at 50% 45%, #e6e6eb 0%, #b8b8be 25%, #6e6e74 55%, #2a2a2e 90%)" }}
      >
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 pt-16 pb-16">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-6">
            / FOR INVESTORS · FOUNDER FEED
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.92] max-w-4xl">
            KSA dealflow,
            <br />
            <span className="italic text-white/70">delivered in gold.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed">
            Pick the sectors you fund. Founder projects in your categories land
            at the top of your feed,
            <span className="text-[#FF9500] font-bold"> glowing gold</span>.
            Out-of-domain projects show up too — many of our investors discover
            adjacent opportunities through the silver tier.
          </p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 bg-[#0c0c0d]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF9500] mb-3">/ JOIN THE FEED</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-8">Free signup · 5 minutes</h2>
          <div className="space-y-5" data-testid="inv-signup-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="text" placeholder="Full name*" value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                data-testid="inv-signup-name" />
              <input type="text" placeholder="Firm (optional)" value={form.firm}
                onChange={(e) => setForm({ ...form, firm: e.target.value })}
                className="bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                data-testid="inv-signup-firm" />
            </div>
            <input type="email" placeholder="Work email*" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
              data-testid="inv-signup-email" />
            <input type="password" placeholder="Password (8+ chars)*" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
              data-testid="inv-signup-password" />
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">
                Categories you invest in*
              </label>
              <div className="flex flex-wrap gap-2" data-testid="inv-signup-cats">
                {categories.map((c) => {
                  const on = form.categories.includes(c.key);
                  return (
                    <button key={c.key} type="button" onClick={() => toggleCat(c.key)}
                      className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border transition-colors ${on ? "bg-[#FF9500] text-black border-[#FF9500]" : "border-white/20 text-white/70 hover:border-white/60 hover:text-white"}`}
                      data-testid={`inv-signup-cat-${c.key}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="number" placeholder="Min cheque USD" value={form.check_min}
                onChange={(e) => setForm({ ...form, check_min: Number(e.target.value) })}
                className="bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white" />
              <input type="number" placeholder="Max cheque USD" value={form.check_max}
                onChange={(e) => setForm({ ...form, check_max: Number(e.target.value) })}
                className="bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white" />
            </div>

            {/* Thesis: stage focus */}
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">
                Stage focus (what stages do you fund?)
              </label>
              <div className="flex flex-wrap gap-2" data-testid="inv-signup-stages">
                {[
                  { key: "idea",     label: "Idea" },
                  { key: "mvp",      label: "MVP" },
                  { key: "revenue",  label: "Revenue" },
                  { key: "scaling",  label: "Scaling" },
                ].map((s) => {
                  const on = form.stage_focus.includes(s.key);
                  return (
                    <button key={s.key} type="button" onClick={() => toggleStage(s.key)}
                      className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border transition-colors ${on ? "bg-[#FF9500] text-black border-[#FF9500]" : "border-white/20 text-white/70 hover:border-white/60 hover:text-white"}`}
                      data-testid={`inv-signup-stage-${s.key}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Thesis: risk appetite */}
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">
                Risk appetite
              </label>
              <div className="flex flex-wrap gap-2" data-testid="inv-signup-risk">
                {[
                  { key: "conservative", label: "Conservative · revenue-only" },
                  { key: "balanced",     label: "Balanced · MVP→revenue" },
                  { key: "aggressive",   label: "Aggressive · idea-stage OK" },
                ].map((r) => {
                  const on = form.risk_appetite === r.key;
                  return (
                    <button key={r.key} type="button" onClick={() => setForm({ ...form, risk_appetite: r.key })}
                      className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border transition-colors ${on ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/60 hover:text-white"}`}
                      data-testid={`inv-signup-risk-${r.key}`}>
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <textarea rows={3} placeholder="Short bio (optional) — what you've backed, sectors you specialize in." value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white" />
            <button type="button" onClick={register} disabled={submitting}
              className="inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-white/85 px-8 py-4 font-bold transition-colors disabled:opacity-50"
              data-testid="inv-signup-submit">
              {submitting ? "Creating…" : "Create investor account"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Sparkles className="w-8 h-8 text-[#FF9500] mx-auto mb-4" />
          <h3 className="font-display font-black text-3xl tracking-tighter mb-4">Looking for funding instead?</h3>
          <Link to="/founders" className="text-[#FF9500] hover:text-white transition-colors font-bold" data-testid="inv-to-founders">
            Submit your project on /founders →
          </Link>
        </div>
      </section>
    </div>
  );
}
